import CreativeEngine, {type XYWH} from "@cesdk/engine";
import type {DesignBlockId} from "@cesdk/cesdk-js";
import {PRESENTATION_HEIGHT, PRESENTATION_WIDTH} from "../constants.ts";
import {getBlockBrightness, setBlockBrightness} from "./slideUtilities.ts";
import type {BlockAction, ClickActionData} from "../api/presentations.ts";
import {findBlockByStableIdForPage, isInteractiveBlockAction} from "./blockActions.ts";
import {writeChoiceVariable} from "./dynamicVariables.ts";


// Module-level storage for toggle state and original properties
// These persist across function calls to maintain toggle state
const toggleState = new Map<string, boolean>();
const originalProperties = new Map<string, any>();

const SELECTION_BRIGHTNESS_RANGE_VERSION_KEY = 'selectionBrightnessRangeVersion';
const CURRENT_SELECTION_BRIGHTNESS_RANGE_VERSION = 2;
const BRIGHTNESS_MIN = -100;
const BRIGHTNESS_MAX = 100;
const LEGACY_BRIGHTNESS_MIN = 0;
const LEGACY_BRIGHTNESS_MAX = 100;
const LEGACY_BRIGHTNESS_MIDPOINT = 50;
const MIN_BOUNDING_BOX_SIZE = 1;

const clamp = (value: number, min: number, max: number): number => Math.max(min, Math.min(max, value));

const legacyBrightnessToCurrent = (legacyBrightness: number): number =>
    clamp((legacyBrightness - LEGACY_BRIGHTNESS_MIDPOINT) * 2, BRIGHTNESS_MIN, BRIGHTNESS_MAX);

const normalizeSelectionBrightness = (rawBrightness: unknown, clickActionData: any): number => {
    const brightness = Number(rawBrightness);
    if (!Number.isFinite(brightness)) {
        return 0;
    }

    const rangeVersion = Number(clickActionData?.[SELECTION_BRIGHTNESS_RANGE_VERSION_KEY]);
    const hasCurrentRange = rangeVersion === CURRENT_SELECTION_BRIGHTNESS_RANGE_VERSION;
    const hasLegacyRangeValue = brightness >= LEGACY_BRIGHTNESS_MIN && brightness <= LEGACY_BRIGHTNESS_MAX;

    if (!hasCurrentRange && hasLegacyRangeValue) {
        return legacyBrightnessToCurrent(brightness);
    }

    return clamp(brightness, BRIGHTNESS_MIN, BRIGHTNESS_MAX);
};

const isUsableBoundingBox = (bbox: XYWH | null | undefined): bbox is XYWH => {
    return Array.isArray(bbox) &&
        bbox.length === 4 &&
        bbox.every((value) => Number.isFinite(value)) &&
        bbox[2] > MIN_BOUNDING_BOX_SIZE &&
        bbox[3] > MIN_BOUNDING_BOX_SIZE;
};

const hasActionableAncestor = (
    engine: CreativeEngine,
    blockId: DesignBlockId,
    actionableBlockIds: Set<DesignBlockId>
): boolean => {
    let currentParent: DesignBlockId | null = null;

    try {
        currentParent = engine.block.getParent?.(blockId) ?? null;
    } catch {
        currentParent = null;
    }

    while (currentParent != null && currentParent !== 0) {
        if (actionableBlockIds.has(currentParent)) {
            return true;
        }

        try {
            currentParent = engine.block.getParent?.(currentParent) ?? null;
        } catch {
            currentParent = null;
        }
    }

    return false;
};


export function getOverlayContainer(): HTMLElement | null
{
    const el = document.getElementById('overlay-container');
    if (el) return el;

    const host = document.getElementById("root-shadow");
    const root: ShadowRoot | null = host?.shadowRoot || (host as any)?.__shadowRoot;
    if (!root) return null;

    return root.getElementById('overlay-container');
}

function resolveActivePageId(engine: CreativeEngine): DesignBlockId | null
{
    const scenePage = engine.scene?.getCurrentPage?.() ?? null;
    const scenePages = engine.scene?.getPages?.() ?? [];
    const visiblePages = scenePages.filter((pageId) => {
        try {
            return engine.block.isValid?.(pageId) && engine.block.isVisible?.(pageId);
        } catch {
            return false;
        }
    });

    if (scenePage != null && visiblePages.includes(scenePage))
        return scenePage;

    if (visiblePages.length === 1)
        return visiblePages[0];

    if (visiblePages.length > 0)
        return visiblePages[0];

    return scenePage;
}

/**
 * Create/update click-action overlays. Overlays appear only:
 *  - on the current page
 *  - during [timeOffset, timeOffset + duration]
 */
export function createClickActionOverlayElements(
    blockActions: BlockAction[],
    opts: {
        container?: HTMLElement | null;
        engine?: CreativeEngine;
        onClick?: (action: BlockAction, slideId: number, ev: MouseEvent) => void;
        activeSlideId?: number | null;
        pageId?: DesignBlockId;
    } = {}
) {
    const container = opts.container ?? getOverlayContainer();
    if (!container) return;

    const engine = opts.engine;
    const activeSlideId = opts.activeSlideId;
    const currentPage = opts.pageId;

    if (!engine) return;

    if (!currentPage) return;

    // --- Resolve current page rect in container coordinates --------------------
    const rect = resolvePageRect(engine, container, currentPage);
    if (!rect) return;

    const { pageLeft, pageTop, pageW, pageH } = rect;

    // --- Page-local time for gating -------------------------------------------
    const now = engine.block.getPlaybackTime(currentPage);
    const EPS = 1e-3;

    // --- Create/update overlays only for blocks on the current page ------------
    const seen = new Set<string>();
    const resolvedEntries: Array<{ entry: BlockAction; engineBlockId: DesignBlockId }> = [];

    const isGroupBlock = (blockId: DesignBlockId): boolean => {
        try {
            return engine.block.getType(blockId).startsWith('//ly.img.ubq/group');
        } catch {
            return false;
        }
    };

    const hasGroupingAncestor = (blockId: DesignBlockId): boolean => {
        let currentParent = engine.block.getParent?.(blockId);

        while (currentParent != null && currentParent !== 0 && engine.block.isValid(currentParent)) {
            if (isGroupBlock(currentParent)) {
                return true;
            }

            try {
                if (engine.block.getType(currentParent) === '//ly.img.ubq/page') {
                    break;
                }
            } catch {
                break;
            }

            currentParent = engine.block.getParent?.(currentParent);
        }

        return false;
    };

    for (const entry of blockActions) {
        if (!activeSlideId || entry.slideId !== activeSlideId) {
            continue;
        }
        if (!isInteractiveBlockAction(entry)) {
            continue;
        }

        const engineBlockId = findBlockByStableIdForPage(engine, currentPage, entry.blockId);
        if (!engineBlockId) {
            continue;
        }
        if (!engine.block.isValid(engineBlockId)) {
            console.warn('missing block action block', entry);
            continue;
        }

        resolvedEntries.push({ entry, engineBlockId });
    }

    const actionableBlockIds = new Set(resolvedEntries.map(({ engineBlockId }) => engineBlockId));

    for (const { entry, engineBlockId } of resolvedEntries) {
        // Grouped elements should defer interaction to their containing group.
        // This suppresses stale child actions that were configured before grouping.
        if (hasGroupingAncestor(engineBlockId) || hasActionableAncestor(engine, engineBlockId, actionableBlockIds)) {
            continue;
        }

        // Time gating - make it more lenient for testing
        const start = Math.max(0, entry.timeOffset ?? 0);
        const end   = start + Math.max(0, entry.duration ?? Number.POSITIVE_INFINITY);
        // For now, ignore time gating to make overlays always visible for testing
        // TODO: re-enable
        const visibleNow = true; // now + EPS >= start && now <= end + EPS;

        // Try to use engine's block bounding box directly for more accurate positioning
        let left = 0;
        let top = 0;
        let w = 0;
        let h = 0;

        let usedEngineBoundingBox = false;
        try {
            if (engine.block.isValid(engineBlockId)) {
                const blockBbox = engine.block.getScreenSpaceBoundingBoxXYWH([engineBlockId]);
                if (isUsableBoundingBox(blockBbox)) {
                    const editorRect = (engine as any).element.getBoundingClientRect();
                    const containerRect = container.getBoundingClientRect();

                    const blockX = blockBbox[0];
                    const blockY = blockBbox[1];
                    const blockW = blockBbox[2];
                    const blockH = blockBbox[3];

                    left = (editorRect.left + blockX) - containerRect.left;
                    top  = (editorRect.top  + blockY) - containerRect.top;
                    w    = blockW;
                    h    = blockH;
                    usedEngineBoundingBox = true;
                }
            }
        } catch (err) {
            console.info('could not get engine block bbox', err);
            usedEngineBoundingBox = false;
        }

        console.log('overlay calc', {
            entry,
            pageLeft,
            pageTop,
            pageW,
            pageH,
            usingEngineBBox: usedEngineBoundingBox,
            // engineBBox: engine?.block?.getScreenSpaceBoundingBoxXYWH?.([engineBlockId])
        });

        // create the element
        const elId = `click-action-overlay-${entry.blockId}`;
        let el = container.querySelector<HTMLDivElement>(`#${CSS.escape(elId)}`);
        if (!el) {
            el = document.createElement('div');
            el.id = elId;
            el.dataset.blockId = entry.blockId;
            if (engineBlockId != null)
                el.dataset.engineBlockId = String(engineBlockId);
            el.className = 'click-action-overlay';
            Object.assign(el.style, {
                position: 'absolute',
                pointerEvents: 'auto',
                cursor: 'pointer'
            });

            if (opts.onClick)
                el.addEventListener('click', (ev) => opts.onClick?.(entry, entry.slideId, ev));

            // Insert click overlays before logo reveal overlays so the logo overlay stays on top
            const logoOverlayRoot: HTMLElement | null = container.querySelector<HTMLElement>('[data-idecide-overlay="logo-reveal"]');
            if (logoOverlayRoot?.parentElement === container) {
                container.insertBefore(el, logoOverlayRoot);
            }
            else if (logoOverlayRoot?.parentElement?.parentElement === container) {
                container.insertBefore(el, logoOverlayRoot?.parentElement);
            } else {
                container.appendChild(el);
            }
        } else {
        }
        seen.add(entry.blockId);


        if (!usedEngineBoundingBox) {
            // Fallback to percentage calculation
            const clamp01 = (v: number) => Math.max(0, Math.min(1, v));
            const xPct = clamp01(entry.x);
            const yPct = clamp01(entry.y);
            const wPct = clamp01(entry.width);
            const hPct = clamp01(entry.height);

            left = pageLeft + xPct * pageW;
            top  = pageTop  + yPct * pageH;
            w    = wPct * pageW;
            h    = hPct * pageH;
        }

        el.style.left   = `${Math.round(left)}px`;
        el.style.top    = `${Math.round(top)}px`;
        el.style.width  = `${Math.round(w)}px`;
        el.style.height = `${Math.round(h)}px`;

        // Visibility & interactivity based on time window
        if (visibleNow) {
            el.style.display = 'block';
            el.style.pointerEvents = 'auto';
        } else {
            el.style.display = 'none';
            el.style.pointerEvents = 'none';
        }

        el.setAttribute('role', 'button');
        el.tabIndex = 0;

        // If it's a Toggle Selection action, wire the behavior
        const resolveChoiceMeta = (action: any): ClickActionData => {
            if (action?.clickActionData && typeof action.clickActionData === 'object') {
                return action.clickActionData as ClickActionData;
            }
            return action as ClickActionData;
        };

        function getSelected(key: string, action: any): boolean {
            const meta = resolveChoiceMeta(action);
            return toggleState.get(key) ?? !!meta?.["selected"];
        }

        function setSelected(
            key: string,
            action: any
        ) {
            const meta = resolveChoiceMeta(action);
            const selected = !getSelected(key, action);
            toggleState.set(key, selected);

            // Optional: store in a variable bag the viewer pipeline can read later.
            const varName = typeof meta?.varName === 'string' ? meta.varName.trim() : '';
            if (meta?.store && varName) {
                const selectedValue = meta?.value ?? 'selected';
                const deselectValue = meta?.deselectValue ?? 'not selected';
                const storedValue = selected ? selectedValue : deselectValue;
                writeChoiceVariable(engine, varName, String(storedValue), meta?.valueType);
            }
        }

        // Check for 'toggleSelection' click action. This is to change the appearance of the selected Imgly block.
        if (entry.clickActionType === 'toggleSelection') {
            el.classList.add('toggle');
            const selected = getSelected(entry.blockId, entry);
            el.setAttribute('aria-pressed', String(selected));

            const commit = (ev?: MouseEvent | KeyboardEvent) => {
                const wasPlaying = currentPage != null && engine.block.isPlaying(currentPage);
                setSelected(entry.blockId, entry);
                const isNowSelected = getSelected(entry.blockId, entry);
                el.setAttribute('aria-pressed', String(isNowSelected));

                // Apply or restore appearance based on toggle state
                try {
                    const styleProperties = entry.clickActionData?.selectionAppearance;
                    if (styleProperties !== undefined && engineBlockId != null) {

                        // Store original properties on first toggle if not already stored
                        if (entry.blockId != null && !originalProperties.has(entry.blockId)) {
                            const original: any = {};
                            for (const key of Object.keys(styleProperties)) {
                                switch (key) {
                                    case 'opacity':
                                        if (engine.block.supportsOpacity(engineBlockId)) {
                                            original.opacity = engine.block.getOpacity(engineBlockId);
                                        }
                                        break;
                                    case 'brightness':
                                        if (engine.block.supportsEffects(engineBlockId)) {
                                            original.brightness = getBlockBrightness(engine, engineBlockId);
                                        }
                                        break;
                                }
                            }
                            originalProperties.set(entry.blockId, original);
                        }

                        if (isNowSelected) {
                            console.log('setting styles', styleProperties);
                            // Apply selection appearance
                            for (const [key, value] of Object.entries(styleProperties)) {
                                switch (key) {
                                    case 'opacity':
                                        if (engine.block.supportsOpacity(engineBlockId)){
                                            const opacity = (Number(value) / 100);
                                            engine.block.setOpacity(engineBlockId, opacity);

                                            if (wasPlaying){
                                                requestAnimationFrame(() => engine.block.setPlaying(currentPage, true));
                                            }
                                        }
                                        break;
                                    case 'brightness':
                                        if (engine.block.supportsEffects(engineBlockId)) {
                                            // Brightness values now use adjustments scale (-100..100, 0 at midpoint).
                                            // Legacy saved actions used 0..100 with 50 at midpoint, so normalize both.
                                            const brightness = normalizeSelectionBrightness(value, entry.clickActionData);
                                            setBlockBrightness(engine, engineBlockId, brightness);

                                            if (wasPlaying){
                                                requestAnimationFrame(() => engine.block.setPlaying(currentPage, true));
                                            }
                                        }
                                        break;
                                }
                            }
                        } else {
                            // Restore original appearance
                            const original = entry.blockId != null ? originalProperties.get(entry.blockId) : null;
                            console.log('original', original, engineBlockId);
                            if (original) {
                                if (original.opacity !== undefined && engine.block.supportsOpacity(engineBlockId)) {
                                    engine.block.setOpacity(engineBlockId, original.opacity);

                                    if (wasPlaying){
                                        requestAnimationFrame(() => engine.block.setPlaying(currentPage, true));
                                    }
                                }
                                if (original.brightness !== undefined && engine.block.supportsEffects(engineBlockId)) {
                                    setBlockBrightness(engine, engineBlockId, original.brightness);

                                    if (wasPlaying){
                                        requestAnimationFrame(() => engine.block.setPlaying(currentPage, true));
                                    }
                                }
                            }
                        }
                    }
                } catch {}
            };

            // Pointer + keyboard
            el.onclick = (ev) => commit(ev);
            el.onkeydown = (ev: KeyboardEvent) => {
                if (ev.key === 'Enter' || ev.key === ' ') {
                    ev.preventDefault();
                    commit(ev);
                }
            };
        }
    }

    // Remove overlays that are no longer relevant (not on this page / deleted)
    container.querySelectorAll<HTMLDivElement>('.click-action-overlay').forEach((el) => {
        const id = el.dataset.blockId;
        if (!id || !seen.has(id)) el.remove();
    });
}

export function repositionClickActionOverlayElements(engine: CreativeEngine)
{
    const container = getOverlayContainer();
    if (!container) return;

    const editorRect: DOMRect = (engine as any).element.getBoundingClientRect();
    const containerRect: DOMRect = container.getBoundingClientRect();

    // loop through any elements in the overlay, resizing them according to the current engine element position/size
    for (const el of Array.from(container.children))
    {
        if (!(el instanceof HTMLDivElement) || !el.classList.contains("click-action-overlay"))
            continue;

        const btn: HTMLDivElement = el as HTMLDivElement;

        // only handle elements with a value engine block
        if (!btn.dataset.engineBlockId)
            continue;

        const engineBlockId: DesignBlockId = Number(btn.dataset.engineBlockId);

        try {
            if (engine.block.isValid(engineBlockId)) {
                const blockBbox = engine.block.getScreenSpaceBoundingBoxXYWH([engineBlockId]);
                if (!isUsableBoundingBox(blockBbox))
                    continue;

                const blockX = blockBbox[0];
                const blockY = blockBbox[1];
                const blockW = blockBbox[2];
                const blockH = blockBbox[3];

                const left = (editorRect.left + blockX) - containerRect.left;
                const top  = (editorRect.top  + blockY) - containerRect.top;

                btn.style.left   = `${Math.round(left)}px`;
                btn.style.top    = `${Math.round(top)}px`;
                btn.style.width  = `${Math.round(blockW)}px`;
                btn.style.height = `${Math.round(blockH)}px`;
            }
        } catch (err) {
            console.warn('could not get engine block bbox for resized click action', err);
        }
    }
}

function resolvePageRect(engine: any, container: HTMLElement, currentPage: number): {pageLeft: number, pageTop: number, pageW: number, pageH: number} | null
{
    let pageLeft = 0, pageTop = 0, pageW = 0, pageH = 0;

    // --- 1. Try to use CE.SDK screen-space bounding box ---------------------
    try {
        const bb = engine.block.getScreenSpaceBoundingBoxXYWH([currentPage]); // [x,y,w,h]
        if (isUsableBoundingBox(bb)) {
            const editorRect = engine.element.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();

            pageLeft = (editorRect.left + bb[0]) - containerRect.left;
            pageTop  = (editorRect.top  + bb[1]) - containerRect.top;
            pageW    = bb[2];
            pageH    = bb[3];
        }
    } catch (_) {
        // Ignore bb failures and fall back
    }

    // --- If we already got a width/height, return early ----------------------
    if (pageW && pageH) {
        return { pageLeft, pageTop, pageW, pageH };
    }

    // --- 2. Fallback: letterbox calculation from canvas ----------------------
    const cesdkHost =
        document.getElementById('cesdk_container') ??
        container.parentElement ??
        document.body;

    const cesdkCanvas = cesdkHost?.querySelector('cesdk-canvas') as
        (HTMLElement & { shadowRoot?: ShadowRoot | null }) | null;

    const shadowCanvas = cesdkCanvas?.shadowRoot?.querySelector('canvas') as HTMLCanvasElement | null;

    const innerCanvas =
        shadowCanvas ||
        container.querySelector('canvas') ||
        container.parentElement?.querySelector('canvas') ||
        document.querySelector('canvas');

    if (!innerCanvas)
        return null;

    const innerRect = innerCanvas.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    const pageAR = PRESENTATION_WIDTH / PRESENTATION_HEIGHT;
    const canvasAR = innerRect.width / innerRect.height;

    if (canvasAR > pageAR) {
        // Canvas wider than page aspect ratio
        pageH = innerRect.height;
        pageW = pageH * pageAR;
        pageLeft = (innerRect.left - containerRect.left) + (innerRect.width - pageW) / 2;
        pageTop  = (innerRect.top  - containerRect.top);
    } else {
        // Canvas taller than page aspect ratio
        pageW = innerRect.width;
        pageH = pageW / pageAR;
        pageLeft = (innerRect.left - containerRect.left);
        pageTop  = (innerRect.top  - containerRect.top) + (innerRect.height - pageH) / 2;
    }

    return (pageW && pageH)
        ? { pageLeft, pageTop, pageW, pageH }
        : null;
}

/**
 * Update HTML overlay visibility to match that of it's matching Imgly block on the video timeline.
 * @param engine
 * @param overlays
 * @param isEngineReady
 */
export function updateOverlayTimelineVisibility(engine: CreativeEngine,
                                                overlays:  Map<number, HTMLDivElement>,
                                                isEngineReady: (engine: any) => boolean) {
    if (!isEngineReady(engine)) {
        return;
    }

    const currentPage = resolveActivePageId(engine);
    const currentPlaybackTime = currentPage ? engine.block.getPlaybackTime(currentPage) : null;
    const overlayContainer = getOverlayContainer();
    const hasVisibleLogoRevealOnCurrentPage = (() => {
        if (!overlayContainer || !currentPage) {
            return false;
        }
        const logoOverlays = overlayContainer.querySelectorAll<HTMLElement>('[data-idecide-overlay="logo-reveal"]');
        for (const logoOverlay of logoOverlays) {
            const owner = Number(logoOverlay.dataset.ownerPage ?? NaN);
            if (owner !== currentPage) {
                continue;
            }
            if (logoOverlay.style.display !== 'none') {
                return true;
            }
        }
        return false;
    })();

    overlays.forEach((el, id) => {
        // Early validation: Check if block still exists BEFORE accessing any properties
        // This prevents errors when blocks are deleted but overlay hasn't been removed yet
        try {
            const isValid = engine.block.isValid?.(id);

            if (!isValid) {
                // Block no longer exists, hide overlay and skip further processing
                el.style.display = 'none';
                return;
            }
        } catch (error) {
            // If we can't even check validity, hide and skip
            el.style.display = 'none';
            return;
        }

        const owner = Number(el.dataset.ownerPage ?? NaN);

        // must be on the current page
        let visible = owner === currentPage;

        // and visible at the current playback time
        const isLogoReveal = el.dataset.idecideOverlay === 'logo-reveal';

        if (visible){
            try {
                visible = engine.block.isVisibleAtCurrentPlaybackTime(id);
            } catch (error) {
                // Assume not visible if we can't check
                visible = false;
            }
        }

        // Keep logo reveal interaction on top by suppressing other overlay types
        // while an active logo reveal is visible on the same page.
        if (visible && !isLogoReveal && hasVisibleLogoRevealOnCurrentPage) {
            visible = false;
        }

        const wasHidden = el.style.display === 'none';
        el.style.display = visible ? 'block' : 'none';

        if (isLogoReveal) {
            if (!visible) {
                if (el.dataset.visibilityNotified === 'true') {
                    delete el.dataset.visibilityNotified;
                }
            } else if (wasHidden || el.dataset.visibilityNotified !== 'true') {
                try {
                    if (typeof window !== 'undefined' && typeof CustomEvent !== 'undefined') {
                        window.dispatchEvent(new CustomEvent('idecide:logo-reveal-visible', {
                            detail: { blockId: id, ownerPage: owner }
                        }));
                    }
                } catch {
                    // Non-fatal if CustomEvent isn't available
                }
                el.dataset.visibilityNotified = 'true';
            }
        }

        // If logo reveal just became visible again, refresh the existing scratch runtime
        // without reinitializing it. Re-init resets the mask and can make the cover reappear
        // when player/review pauses and rewinds slightly at the end of the slide.
        if (isLogoReveal && visible && wasHidden) {
            requestAnimationFrame(() => {
                try {
                    const content = el.querySelector('[data-role="content"]') as HTMLElement;
                    if (content) {
                        const scratchSettings = (content as any).__scratchSettings;
                        const coverScratch = (window as any).CoverScratch;

                        if (scratchSettings && coverScratch?.updateSettings) {
                            coverScratch.updateSettings(scratchSettings.settings);
                        } else if (coverScratch?.resize) {
                            coverScratch.resize();
                        } else {
                            console.warn('[RubScreen] Cannot refresh scratch layout - CoverScratch not available');
                        }
                    }
                } catch (e) {
                    console.error('[RubScreen] CoverScratch refresh failed:', e);
                }
            });
        }

        // disable input when hidden so it never eats clicks
        (el.querySelector('input[data-role="input"]') as HTMLInputElement | null)
            ?.toggleAttribute('disabled', !visible);
    });
}

/** Position an overlay (wrap) so it matches a design block in screen space. */
export function positionOverlayToBlock(
    engine: CreativeEngine,
    blockId: number,
    el: HTMLElement,
    updateElementGeometry: any
): void {
    // Safety check: engine might be disposed during navigation
    if (!engine || !engine.block) {
        console.warn('[positionOverlayToBlock] Engine not available, skipping positioning');
        return;
    }

    if (!Number.isInteger(blockId) || blockId <= 0 || blockId === 4294967295) {
        el.style.display = 'none';
        return;
    }

    try {
        if (engine.block.isValid?.(blockId) === false) {
            el.style.display = 'none';
            return;
        }
    } catch {
        el.style.display = 'none';
        return;
    }

    let raw: any;
    try {
        raw = (engine.block as any).getScreenSpaceBoundingBoxXYWH?.([blockId]);
    } catch {
        // Slide virtualization can purge a block after layout work is scheduled.
        el.style.display = 'none';
        return;
    }
    if (!raw) return;

    // Accept both array and object return shapes
    const x = Array.isArray(raw) ? raw[0] : raw.x;
    const y = Array.isArray(raw) ? raw[1] : raw.y;
    const w = Array.isArray(raw) ? raw[2] : raw.width;
    const h = Array.isArray(raw) ? raw[3] : raw.height;

    const editorRect = (engine as any).element?.getBoundingClientRect?.();
    const hostRect = el.parentElement?.getBoundingClientRect?.(); // overlay root
    if (!editorRect || !hostRect) return;

    // Convert editor-space bbox -> host-space coords
    const left = (editorRect.left + x) - hostRect.left;
    const top  = (editorRect.top  + y) - hostRect.top;

    el.style.transform = '';
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
    el.style.width = `${w}px`;
    el.style.height = `${h}px`;

    updateElementGeometry(el, engine, blockId); // rescale pill geometry to the block height
}
